/**
 * useIsBroadcastingLive — a reactive read of the cross-tab
 * `liveBroadcasterPresence` ledger.
 *
 * Answers "is `userId` currently broadcasting a LIVE session (in ANY same-origin
 * tab)?" and re-evaluates whenever that could change:
 *
 * - the cross-tab `window` `'storage'` event — fires in OTHER tabs when Tab A
 *   marks / clears its broadcaster presence, so a community composer rendered in
 *   Tab B disables / re-enables its go-live without a poll; and
 * - `visibilitychange` → visible — a belt-and-suspenders same-tab return path
 *   (the `'storage'` event does NOT fire in the tab that made the write), reusing
 *   the shared `subscribeToTabVisibility` utility rather than a second raw
 *   listener.
 *
 * SSR-safe: `userId` may be `undefined` (unauthenticated / pre-hydration) → the
 * hook reports `false` and registers no listeners until an id is available.
 */
import { useEffect, useState } from 'react';
import { subscribeToTabVisibility } from '@/presentation/utils/tabVisibility';
import { isBroadcastingLive } from '@/presentation/feature/liveStreaming/utils/liveBroadcasterPresence';

/**
 * @param userId - the current user's firebase id, or `undefined` when there is no
 * authenticated user yet.
 * @returns `true` while `userId` has an active broadcaster-presence entry, else
 * `false`.
 */
export function useIsBroadcastingLive(userId: string | undefined): boolean {
  const [broadcasting, setBroadcasting] = useState<boolean>(() =>
    userId === undefined ? false : isBroadcastingLive(userId),
  );

  useEffect(() => {
    if (userId === undefined) {
      setBroadcasting(false);
      return;
    }

    const reevaluate = () => {
      setBroadcasting(isBroadcastingLive(userId));
    };

    // Sync immediately in case the ledger changed between render and effect.
    reevaluate();

    // Cross-tab: `'storage'` fires in the OTHER tabs when localStorage is written.
    // Re-read on any change — `isBroadcastingLive` reads fresh and an identical
    // boolean `setState` bails out of re-render, so no key filter is needed (and
    // it avoids duplicating the util's private storage-key constant here).
    const handleStorage = () => {
      reevaluate();
    };
    window.addEventListener('storage', handleStorage);

    // Same-tab return path: the `'storage'` event never fires in the writing tab.
    const unsubscribeVisibility = subscribeToTabVisibility((isVisible) => {
      if (isVisible) {
        reevaluate();
      }
    });

    return () => {
      window.removeEventListener('storage', handleStorage);
      unsubscribeVisibility();
    };
  }, [userId]);

  return broadcasting;
}
